import React, { useState, useEffect } from 'react';
import './RewardRecognitionManagement.css';
import rewardRecognitionService, { 
  RewardCategory, 
  RewardSubcategory, 
  RewardRecognitionWithDetails,
  CreateRewardRecognitionData 
} from '../services/rewardRecognitionService';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import MonthYearPicker from './MonthYearPicker';
import usersService, { User } from '../services/usersService';

interface RewardRecognitionManagementProps {
  onClose?: () => void;
}

const RewardRecognitionManagement: React.FC<RewardRecognitionManagementProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<'recognitions' | 'categories' | 'subcategories'>('recognitions');
  const [categories, setCategories] = useState<RewardCategory[]>([]);
  const [subcategories, setSubcategories] = useState<RewardSubcategory[]>([]);
  const [recognitions, setRecognitions] = useState<RewardRecognitionWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form states
  const [showRecognitionForm, setShowRecognitionForm] = useState(false);
  const [editingRecognition, setEditingRecognition] = useState<RewardRecognitionWithDetails | null>(null);
  const [recognitionForm, setRecognitionForm] = useState<CreateRewardRecognitionData>({
    category_id: 0,
    subcategory_id: 0,
    employee_id: '',
    employee_name: '',
    employee_email: '',
    employee_photo: '',
    award_date: new Date().toISOString().split('T')[0],
    description: '',
    achievement_details: '',
    is_featured: false
  });

  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<RewardCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color_code: '#0078d4',
    icon: '',
    is_active: true,
    sort_order: 0
  });

  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<RewardSubcategory | null>(null);
  const [subcategoryForm, setSubcategoryForm] = useState({
    category_id: 0,
    name: '',
    description: '',
    is_active: true,
    sort_order: 0
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recognitionToDelete, setRecognitionToDelete] = useState<RewardRecognitionWithDetails | null>(null);
  const [showDeleteSubcategoryModal, setShowDeleteSubcategoryModal] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState<RewardSubcategory | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [awardMonthYear, setAwardMonthYear] = useState<string>(''); // Format: YYYY-MM
  const [employees, setEmployees] = useState<User[]>([]);

  useEffect(() => {
    loadData();
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const users = await usersService.getAllUsers();
      const activeUsers = users.filter(user => user.isActive);
      console.log('Loaded employees:', activeUsers);
      setEmployees(activeUsers);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [categoriesData, subcategoriesData, recognitionsData] = await Promise.all([
        rewardRecognitionService.getCategories(),
        rewardRecognitionService.getSubcategories(),
        rewardRecognitionService.getRewardRecognitions()
      ]);
      
      setCategories(categoriesData);
      setSubcategories(subcategoriesData);
      setRecognitions(recognitionsData);
      setImageRefreshKey(Date.now()); // Update refresh key when data is fetched
    } catch (error) {
      console.error('Error loading data:', error);
      showMessage('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Recognition Management
  const handleRecognitionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Submitting form with data:', recognitionForm);
    
    // Validate required fields
    if (!recognitionForm.category_id || recognitionForm.category_id === 0) {
      showMessage('error', 'Please select a category');
      return;
    }
    
    if (!recognitionForm.subcategory_id || recognitionForm.subcategory_id === 0) {
      showMessage('error', 'Please select a subcategory');
      return;
    }
    
    if (!recognitionForm.employee_name || !recognitionForm.employee_email) {
      showMessage('error', 'Please select an employee');
      return;
    }
    
    if (!recognitionForm.award_date) {
      showMessage('error', 'Please select an award month/year');
      return;
    }
    
    if (!photoFile && !editingRecognition) {
      showMessage('error', 'Please upload an employee photo');
      return;
    }
    
    setLoading(true);

    try {
      const formData = new FormData();
      
      // Append all form fields
      formData.append('category_id', String(recognitionForm.category_id));
      formData.append('subcategory_id', String(recognitionForm.subcategory_id));
      formData.append('employee_id', recognitionForm.employee_id);
      formData.append('employee_name', recognitionForm.employee_name);
      formData.append('employee_email', recognitionForm.employee_email);
      formData.append('award_date', recognitionForm.award_date);
      formData.append('description', recognitionForm.description);
      formData.append('achievement_details', recognitionForm.achievement_details);
      formData.append('is_featured', String(recognitionForm.is_featured));
      
      console.log('FormData being sent:', {
        category_id: recognitionForm.category_id,
        subcategory_id: recognitionForm.subcategory_id,
        employee_id: recognitionForm.employee_id,
        employee_name: recognitionForm.employee_name,
        employee_email: recognitionForm.employee_email,
        award_date: recognitionForm.award_date,
        has_photo: !!photoFile
      });
      
      // Append photo file if selected
      if (photoFile) {
        formData.append('employee_photo', photoFile);
      }

      let result;
      if (editingRecognition) {
        result = await rewardRecognitionService.updateRewardRecognition(editingRecognition.id, formData);
      } else {
        result = await rewardRecognitionService.createRewardRecognition(formData);
      }

      if (result) {
        showMessage('success', `Recognition ${editingRecognition ? 'updated' : 'created'} successfully`);
        resetRecognitionForm();
        loadData();
      } else {
        showMessage('error', `Failed to ${editingRecognition ? 'update' : 'create'} recognition`);
      }
    } catch (error: any) {
      console.error('Error saving recognition:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'An error occurred while saving';
      showMessage('error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecognitionClick = (recognition: RewardRecognitionWithDetails) => {
    setRecognitionToDelete(recognition);
    setShowDeleteModal(true);
  };

  const handleDeleteRecognitionConfirm = async () => {
    if (!recognitionToDelete) return;

    setLoading(true);
    try {
      const success = await rewardRecognitionService.deleteRewardRecognition(recognitionToDelete.id);
      if (success) {
        showMessage('success', 'Recognition deleted successfully');
        loadData();
      } else {
        showMessage('error', 'Failed to delete recognition');
      }
    } catch (error) {
      console.error('Error deleting recognition:', error);
      showMessage('error', 'An error occurred while deleting');
    } finally {
      setLoading(false);
      setRecognitionToDelete(null);
    }
  };

  const resetRecognitionForm = () => {
    setRecognitionForm({
      category_id: 0,
      subcategory_id: 0,
      employee_id: '',
      employee_name: '',
      employee_email: '',
      employee_photo: '',
      award_date: '',
      description: '',
      achievement_details: '',
      is_featured: false
    });
    setEditingRecognition(null);
    setShowRecognitionForm(false);
    setPhotoFile(null);
    setSelectedEmployeeId('');
    setAwardMonthYear('');
    setPhotoPreview('');
  };

  const handleEditRecognition = (recognition: RewardRecognitionWithDetails) => {
    setEditingRecognition(recognition);
    setRecognitionForm({
      category_id: recognition.category_id,
      subcategory_id: recognition.subcategory_id,
      employee_id: recognition.employee_id,
      employee_name: recognition.employee_name,
      employee_email: recognition.employee_email,
      employee_photo: recognition.employee_photo || '',
      award_date: recognition.award_date,
      description: recognition.description,
      achievement_details: recognition.achievement_details,
      is_featured: recognition.is_featured
    });
    setSelectedEmployeeId(recognition.employee_id);
    // Parse award_date to YYYY-MM format
    const date = new Date(recognition.award_date);
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    setAwardMonthYear(monthYear);
    // Don't set photoPreview here - we'll show current image separately
    setPhotoFile(null);
    setShowRecognitionForm(true);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMessage('error', 'Please select an image file');
        return;
      }
      
      // Validate file size (25MB max)
      if (file.size > 50 * 1024 * 1024) {
        showMessage('error', 'Image size must be less than 25MB');
        return;
      }
      
      setPhotoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const handleEmployeeSelect = (identifier: string) => {
    console.log('Selected identifier:', identifier);
    console.log('All employees:', employees);
    
    setSelectedEmployeeId(identifier);
    
    // Try to find by employeeId first, then by email as fallback
    const employee = employees.find(emp => 
      emp.employeeId === identifier || emp.email === identifier
    );
    
    console.log('Found employee:', employee);
    
    if (employee) {
      const updatedForm = {
        ...recognitionForm,
        employee_id: employee.employeeId || employee.email || '',
        employee_name: employee.employeeName,
        employee_email: employee.email,
        employee_photo: '' // Will be uploaded separately
      };
      
      console.log('Updated form:', updatedForm);
      setRecognitionForm(updatedForm);
    } else {
      console.error('Employee not found for identifier:', identifier);
    }
  };

  const handleMonthYearChange = (value: string) => {
    console.log('Month/Year picker value:', value);
    setAwardMonthYear(value);
    if (value) {
      // Format: YYYY-MM, create date as first day of the selected month
      const [year, month] = value.split('-');
      console.log('Parsed year:', year, 'month:', month);
      
      // Create date string directly to avoid timezone issues
      const formattedDate = `${year}-${month.padStart(2, '0')}-01`;
      console.log('Created award_date:', formattedDate);
      
      setRecognitionForm({
        ...recognitionForm,
        award_date: formattedDate
      });
    }
  };


  const getFilteredSubcategories = (categoryId: number) => {
    return subcategories.filter(sub => sub.category_id === categoryId);
  };

  // Subcategory Management Functions
  const handleSubcategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (editingSubcategory) {
        result = await rewardRecognitionService.updateSubcategory(editingSubcategory.id, subcategoryForm);
      } else {
        result = await rewardRecognitionService.createSubcategory(subcategoryForm);
      }

      if (result) {
        showMessage('success', `Subcategory ${editingSubcategory ? 'updated' : 'created'} successfully`);
        resetSubcategoryForm();
        loadData();
      } else {
        showMessage('error', `Failed to ${editingSubcategory ? 'update' : 'create'} subcategory`);
      }
    } catch (error) {
      console.error('Error saving subcategory:', error);
      showMessage('error', 'An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubcategory = (subcategory: RewardSubcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      category_id: subcategory.category_id,
      name: subcategory.name,
      description: subcategory.description || '',
      is_active: subcategory.is_active,
      sort_order: subcategory.sort_order
    });
    setShowSubcategoryForm(true);
  };

  const handleDeleteSubcategoryClick = (subcategory: RewardSubcategory) => {
    setSubcategoryToDelete(subcategory);
    setShowDeleteSubcategoryModal(true);
  };

  const handleDeleteSubcategoryConfirm = async () => {
    if (!subcategoryToDelete) return;

    setLoading(true);
    try {
      const success = await rewardRecognitionService.deleteSubcategory(subcategoryToDelete.id);
      if (success) {
        showMessage('success', 'Subcategory deleted successfully');
        loadData();
      } else {
        showMessage('error', 'Failed to delete subcategory');
      }
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      showMessage('error', 'An error occurred while deleting');
    } finally {
      setLoading(false);
      setSubcategoryToDelete(null);
    }
  };

  const resetSubcategoryForm = () => {
    setSubcategoryForm({
      category_id: 0,
      name: '',
      description: '',
      is_active: true,
      sort_order: 0
    });
    setEditingSubcategory(null);
    setShowSubcategoryForm(false);
  };

  return (
    <div className="reward-recognition-management">
      <div className="management-header">
        <h2>Reward & Recognition Management</h2>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="management-tabs">
        <button
          className={`tab-btn ${activeTab === 'recognitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('recognitions')}
        >
          Recognitions
        </button>
        <button
          className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </button>
        <button
          className={`tab-btn ${activeTab === 'subcategories' ? 'active' : ''}`}
          onClick={() => setActiveTab('subcategories')}
        >
          Subcategories
        </button>
      </div>

      <div className="management-content">
        {/* Recognitions Tab */}
        {activeTab === 'recognitions' && (
          <div className="recognitions-tab">
            <div className="tab-header">
              <h3>Manage Recognitions</h3>
              <button
                className="add-btn"
                onClick={() => setShowRecognitionForm(true)}
                disabled={loading}
              >
                Add Recognition
              </button>
            </div>

            {showRecognitionForm && (
              <div className="form-modal">
                <div className="form-container">
                  <div className="form-header">
                    <h4>{editingRecognition ? 'Edit Recognition' : 'Add New Recognition'}</h4>
                    <button className="close-btn" onClick={resetRecognitionForm}>×</button>
                  </div>

                  <form onSubmit={handleRecognitionSubmit} className="recognition-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Category *</label>
                        <select
                          value={recognitionForm.category_id}
                          onChange={(e) => {
                            const categoryId = parseInt(e.target.value);
                            setRecognitionForm({
                              ...recognitionForm,
                              category_id: categoryId,
                              subcategory_id: 0
                            });
                          }}
                          required
                        >
                          <option value={0}>Select Category</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Subcategory *</label>
                        <select
                          value={recognitionForm.subcategory_id}
                          onChange={(e) => setRecognitionForm({
                            ...recognitionForm,
                            subcategory_id: parseInt(e.target.value)
                          })}
                          required
                          disabled={!recognitionForm.category_id}
                        >
                          <option value={0}>Select Subcategory</option>
                          {getFilteredSubcategories(recognitionForm.category_id).map(sub => (
                            <option key={sub.id} value={sub.id}>{sub.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Select Employee *</label>
                        <select
                          value={selectedEmployeeId}
                          onChange={(e) => handleEmployeeSelect(e.target.value)}
                          required
                        >
                          <option value="">Select Employee</option>
                          {employees.map(emp => (
                            <option key={emp.id} value={emp.employeeId || emp.email}>
                              {emp.employeeName} ({emp.employeeId || emp.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Employee ID</label>
                        <input
                          type="text"
                          value={recognitionForm.employee_id}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Employee Name</label>
                        <input
                          type="text"
                          value={recognitionForm.employee_name}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Employee Email</label>
                        <input
                          type="email"
                          value={recognitionForm.employee_email}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <MonthYearPicker
                          value={awardMonthYear}
                          onChange={handleMonthYearChange}
                          label="Award Month & Year"
                          required={true}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Employee Photo</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Recommended size: 300×300 pixels (1:1 ratio). Max 5MB.
                      </p>
                      {editingRecognition?.employee_photo && !photoFile && (
                        <div style={{ marginTop: '8px' }}>
                          <img 
                            src={`${rewardRecognitionService.getPhotoUrl(editingRecognition.employee_photo)}?t=${imageRefreshKey}`}
                            alt="Current"
                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                            onError={(e) => {
                              console.error('Failed to load image:', editingRecognition.employee_photo);
                              (e.target as HTMLImageElement).src = '/assets/images/profile-avatar.jpg';
                            }}
                          />
                          <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Current image</p>
                        </div>
                      )}
                      {photoFile && (
                        <div style={{ marginTop: '8px' }}>
                          <p style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>New Image (Cropped):</p>
                          <img 
                            src={URL.createObjectURL(photoFile)}
                            alt="Cropped preview"
                            style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', border: '2px solid #00a0e3' }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Description *</label>
                      <textarea
                        value={recognitionForm.description}
                        onChange={(e) => setRecognitionForm({
                          ...recognitionForm,
                          description: e.target.value
                        })}
                        required
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label>Achievement Details</label>
                      <textarea
                        value={recognitionForm.achievement_details}
                        onChange={(e) => setRecognitionForm({
                          ...recognitionForm,
                          achievement_details: e.target.value
                        })}
                        rows={4}
                      />
                    </div>

                    <div className="form-group checkbox-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={recognitionForm.is_featured}
                          onChange={(e) => setRecognitionForm({
                            ...recognitionForm,
                            is_featured: e.target.checked
                          })}
                          style={{ margin: 0, cursor: 'pointer' }}
                        />
                        <span>Featured Recognition</span>
                      </label>
                    </div>

                    <div className="form-actions">
                      <button type="button" onClick={resetRecognitionForm} disabled={loading}>
                        Cancel
                      </button>
                      <button type="submit" disabled={loading}>
                        {loading ? 'Saving...' : (editingRecognition ? 'Update' : 'Create')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="recognitions-list">
              {loading ? (
                <div className="loading">Loading recognitions...</div>
              ) : recognitions.length === 0 ? (
                <div className="no-data">
                  <p>No recognitions found. Click "Add Recognition" to create one.</p>
                </div>
              ) : (
                <div style={{ marginTop: '20px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Photo</th>
                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '18%' }}>Employee</th>
                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Category</th>
                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Subcategory</th>
                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '25%' }}>Description</th>
                        <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Award Month</th>
                        <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '13%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recognitions.map(recognition => (
                        <tr key={recognition.id} style={{ borderBottom: '1px solid #ddd' }}>
                          <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                            <div style={{ position: 'relative' }}>
                              <img 
                                src={recognition.employee_photo ? 
                                  `${rewardRecognitionService.getPhotoUrl(recognition.employee_photo)}?t=${imageRefreshKey}` : 
                                  '/assets/images/profile-avatar.jpg'
                                }
                                alt={recognition.employee_name}
                                style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/assets/images/profile-avatar.jpg';
                                }}
                              />
                              {recognition.is_featured && (
                                <span style={{
                                  position: 'absolute',
                                  top: '-5px',
                                  right: '-5px',
                                  fontSize: '16px',
                                  color: '#FFD700'
                                }} title="Featured Recognition">★</span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                            <div>
                              <strong>{recognition.employee_name}</strong>
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                {recognition.employee_id}
                              </div>
                              <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                                {recognition.employee_email}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                            <span style={{
                              backgroundColor: recognition.category?.color_code,
                              color: 'white',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              display: 'inline-block',
                              wordBreak: 'break-word'
                            }}>
                              {recognition.category?.name}
                            </span>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                            <span style={{
                              backgroundColor: '#f0f0f0',
                              color: '#333',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              display: 'inline-block',
                              wordBreak: 'break-word'
                            }}>
                              {recognition.subcategory?.name}
                            </span>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              {recognition.description.substring(0, 100)}{recognition.description.length > 100 ? '...' : ''}
                            </span>
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                            {new Date(recognition.award_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short'
                            })}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: loading ? 'not-allowed' : 'pointer',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '16px',
                                  transition: 'all 0.3s ease',
                                  opacity: loading ? 0.5 : 1
                                }}
                                onClick={() => handleEditRecognition(recognition)}
                                disabled={loading}
                                title="Edit recognition"
                                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#e3f2fd')}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                ✏️
                              </button>
                              <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: loading ? 'not-allowed' : 'pointer',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '16px',
                                  transition: 'all 0.3s ease',
                                  opacity: loading ? 0.5 : 1
                                }}
                                onClick={() => handleDeleteRecognitionClick(recognition)}
                                disabled={loading}
                                title="Delete recognition"
                                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#ffebee')}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Categories and Subcategories tabs would be similar but simpler */}
        {activeTab === 'categories' && (
          <div className="categories-tab">
            <div className="tab-header">
              <h3>Manage Categories</h3>
              <p>Categories are managed through the database. Current categories: Reliability, Excellence, Agility, Perseverance</p>
            </div>
            <div className="categories-list">
              {categories.map(category => (
                <div key={category.id} className="category-item">
                  <div 
                    className="category-color" 
                    style={{ backgroundColor: category.color_code }}
                  ></div>
                  <div className="category-info">
                    <h4>{category.name}</h4>
                    <p>{category.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'subcategories' && (
          <div className="subcategories-tab">
            <div className="tab-header">
              <h3>Manage Subcategories</h3>
              <button
                className="add-btn"
                onClick={() => setShowSubcategoryForm(true)}
                disabled={loading}
              >
                Add Subcategory
              </button>
            </div>

            {showSubcategoryForm && (
              <div className="form-modal">
                <div className="form-container">
                  <div className="form-header">
                    <h4>{editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}</h4>
                    <button className="close-btn" onClick={resetSubcategoryForm}>×</button>
                  </div>

                  <form onSubmit={handleSubcategorySubmit} className="subcategory-form">
                    <div className="form-group">
                      <label>Category *</label>
                      <select
                        value={subcategoryForm.category_id}
                        onChange={(e) => setSubcategoryForm({
                          ...subcategoryForm,
                          category_id: parseInt(e.target.value)
                        })}
                        required
                      >
                        <option value={0}>Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Subcategory Name *</label>
                      <input
                        type="text"
                        value={subcategoryForm.name}
                        onChange={(e) => setSubcategoryForm({
                          ...subcategoryForm,
                          name: e.target.value
                        })}
                        required
                        placeholder="Enter subcategory name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={subcategoryForm.description}
                        onChange={(e) => setSubcategoryForm({
                          ...subcategoryForm,
                          description: e.target.value
                        })}
                        rows={3}
                        placeholder="Enter subcategory description"
                      />
                    </div>

                    <div className="form-group">
                      <label>Sort Order</label>
                      <input
                        type="number"
                        value={subcategoryForm.sort_order}
                        onChange={(e) => setSubcategoryForm({
                          ...subcategoryForm,
                          sort_order: parseInt(e.target.value) || 0
                        })}
                        min="0"
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group checkbox-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={subcategoryForm.is_active}
                          onChange={(e) => setSubcategoryForm({
                            ...subcategoryForm,
                            is_active: e.target.checked
                          })}
                          style={{ margin: 0, cursor: 'pointer' }}
                        />
                        <span>Active</span>
                      </label>
                    </div>

                    <div className="form-actions">
                      <button type="button" onClick={resetSubcategoryForm} disabled={loading}>
                        Cancel
                      </button>
                      <button type="submit" disabled={loading}>
                        {loading ? 'Saving...' : (editingSubcategory ? 'Update' : 'Create')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="subcategories-list">
              {loading ? (
                <div className="loading">Loading subcategories...</div>
              ) : (
                categories.map(category => (
                  <div key={category.id} className="category-section">
                    <div className="category-section-header">
                      <h4 style={{ color: category.color_code }}>{category.name}</h4>
                      <button
                        className="add-subcategory-btn"
                        onClick={() => {
                          setSubcategoryForm({
                            ...subcategoryForm,
                            category_id: category.id
                          });
                          setShowSubcategoryForm(true);
                        }}
                        disabled={loading}
                      >
                        + Add to {category.name}
                      </button>
                    </div>
                    <div className="subcategories-grid">
                      {getFilteredSubcategories(category.id).map(subcategory => (
                        <div key={subcategory.id} className="subcategory-item">
                          <div className="subcategory-header">
                            <h5>{subcategory.name}</h5>
                            <div className="subcategory-actions">
                              <button
                                className="edit-btn"
                                onClick={() => handleEditSubcategory(subcategory)}
                                disabled={loading}
                                title="Edit subcategory"
                              >
                                ✏️
                              </button>
                              <button
                                className="delete-btn"
                                onClick={() => handleDeleteSubcategoryClick(subcategory)}
                                disabled={loading}
                                title="Delete subcategory"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                          <p>{subcategory.description}</p>
                          <div className="subcategory-meta">
                            <span className="sort-order">Order: {subcategory.sort_order}</span>
                            <span className={`status ${subcategory.is_active ? 'active' : 'inactive'}`}>
                              {subcategory.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      ))}
                      {getFilteredSubcategories(category.id).length === 0 && (
                        <div className="no-subcategories">
                          <p>No subcategories in this category</p>
                          <button
                            className="add-first-btn"
                            onClick={() => {
                              setSubcategoryForm({
                                ...subcategoryForm,
                                category_id: category.id
                              });
                              setShowSubcategoryForm(true);
                            }}
                          >
                            Add First Subcategory
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Recognition Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteRecognitionConfirm}
        title="Delete Recognition"
        message="Are you sure you want to delete this recognition?"
        itemName={recognitionToDelete?.employee_name}
      />

      {/* Delete Subcategory Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteSubcategoryModal}
        onClose={() => setShowDeleteSubcategoryModal(false)}
        onConfirm={handleDeleteSubcategoryConfirm}
        title="Delete Subcategory"
        message="Are you sure you want to delete this subcategory? This action cannot be undone."
        itemName={subcategoryToDelete?.name}
      />
    </div>
  );
};

export default RewardRecognitionManagement;
